import { Box, Collapse, IconButton, Typography, useTheme } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { tokens } from '../../../../global/theme/tokens';
import { renderHours } from '../../../../scenes/time-tracking/worktime';
import ProjectTaskWorkPackage from './ProjectTaskWorkPackage';
import { isWorkPackageVisible } from './taskFilterQuery';

// estimatedTime and totalTimeWorkedSeconds are both seconds - renderHours
// takes minutes, the same conversion EstimatedTime uses per work package
const TimeStat = ({ label, seconds, color }) => (
  <Box className="flex flex-col items-end">
    <Typography fontSize="10px" className="uppercase opacity-50">
      {label}
    </Typography>
    <Typography
      fontSize="12px"
      fontWeight={500}
      className="monospace"
      sx={{ color: color || 'inherit' }}
    >
      {`${renderHours(seconds / 60)} h`}
    </Typography>
  </Box>
);

const ProjectTaskPhase = ({
  project,
  phase,
  filter,
  onLoadingChange,
  onStatsChange,
  defaultExpanded = false,
  // 'glass' sits on the page background, 'card' on top of another card - the
  // same surface the work packages inside the phase use
  variant = 'glass',
  allowCreate = true,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(defaultExpanded);

  const workPackages = useMemo(
    () => phase.workPackages || [],
    [phase.workPackages],
  );

  // hideEmpty keeps work packages with nothing to list off the screen
  const shownWorkPackages = useMemo(
    () =>
      workPackages.filter((workPackage) =>
        isWorkPackageVisible(workPackage, filter || {}),
      ),
    [workPackages, filter],
  );

  // phase totals: the sum over all of its work packages, hidden ones
  // included - the hours of the phase do not change with the filter
  const { plannedSeconds, workedSeconds } = useMemo(
    () =>
      workPackages.reduce(
        (sum, wp) => ({
          plannedSeconds: sum.plannedSeconds + (wp.estimatedTime || 0),
          workedSeconds: sum.workedSeconds + (wp.totalTimeWorkedSeconds || 0),
        }),
        { plannedSeconds: 0, workedSeconds: 0 },
      ),
    [workPackages],
  );

  const isOverPlanned = plannedSeconds > 0 && workedSeconds > plannedSeconds;

  return (
    <Box
      className="rounded-xl overflow-hidden backdrop-blur-lg"
      sx={
        variant === 'card'
          ? {
              backgroundColor: colors.card,
              border: `1px solid ${colors.text}12`,
            }
          : { backgroundColor: colors.glass }
      }
    >
      <Box
        onClick={() => setIsOpen(!isOpen)}
        className="flex flex-wrap items-center gap-x-2 gap-y-1 px-2 sm:px-3 py-1.5 cursor-pointer"
        sx={{ ':hover': { backgroundColor: `${colors.text}08` } }}
      >
        <IconButton
          size="small"
          onClick={() => setIsOpen(!isOpen)}
          className="shrink-0"
        >
          <KeyboardArrowRightIcon
            sx={{
              fontSize: '20px',
              transform: `rotate(${isOpen ? 90 : 0}deg)`,
              transition: 'transform 200ms',
            }}
          />
        </IconButton>

        <Box className="flex flex-col grow min-w-0">
          <Typography
            fontWeight={600}
            sx={{
              fontSize: { xs: '14px', sm: '15px' },
              lineHeight: 1.3,
              wordBreak: 'break-word',
            }}
          >
            {phase.name}
          </Typography>
          <Typography fontSize="10px" className="opacity-60">
            {`${shownWorkPackages.length} ${t('Work Packages')}`}
          </Typography>
        </Box>

        {/* on a phone the times get their own row instead of squeezing the name */}
        <Box className="flex items-center justify-end gap-4 shrink-0 w-full sm:w-auto pr-2 order-last sm:order-none">
          <TimeStat label={t('Planed')} seconds={plannedSeconds} />
          <TimeStat
            label={t('Worked')}
            seconds={workedSeconds}
            color={isOverPlanned ? colors.error : null}
          />
        </Box>
      </Box>

      <Collapse in={isOpen} unmountOnExit>
        <Box className="flex flex-col gap-2 px-1 sm:px-3 pb-3">
          {shownWorkPackages.length ? (
            shownWorkPackages.map((workPackage) => (
              <ProjectTaskWorkPackage
                key={workPackage.id}
                project={project}
                workPackage={workPackage}
                filter={filter}
                onLoadingChange={onLoadingChange}
                onStatsChange={onStatsChange}
                allowCreate={allowCreate}
              />
            ))
          ) : (
            <Typography fontSize="13px" className="opacity-50 py-2 px-2">
              {t('No work packages')}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default ProjectTaskPhase;
